import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ArrowLeft, AlertCircle, CheckCircle, Loader2, User, Mail, Phone, Lock, RefreshCw, Eye, EyeOff } from 'lucide-react';

const SignUp = ({ onBackToLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [] });

  // Password requirements
  const passwordRequirements = {
    minLength: 8,
    hasUppercase: /[A-Z]/,
    hasLowercase: /[a-z]/,
    hasNumber: /[0-9]/,
    hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\?]/
  };

  const generateStrongPassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    
    let password = '';
    
    // Ensure at least one character from each category
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Fill the rest randomly (12 total characters)
    const allChars = uppercase + lowercase + numbers + symbols;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    const shuffled = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setFormData(prev => ({ ...prev, password: shuffled }));
    validatePassword(shuffled);
  };

  const validatePassword = (password) => {
    const feedback = [];
    let score = 0;

    if (password.length >= passwordRequirements.minLength) {
      score += 1;
    } else {
      feedback.push(`At least ${passwordRequirements.minLength} characters`);
    }

    if (passwordRequirements.hasUppercase.test(password)) {
      score += 1;
    } else {
      feedback.push('One uppercase letter (A-Z)');
    }

    if (passwordRequirements.hasLowercase.test(password)) {
      score += 1;
    } else {
      feedback.push('One lowercase letter (a-z)');
    }

    if (passwordRequirements.hasNumber.test(password)) {
      score += 1;
    } else {
      feedback.push('One number (0-9)');
    }

    if (passwordRequirements.hasSymbol.test(password)) {
      score += 1;
    } else {
      feedback.push('One symbol (!@#$%^&*...)');
    }

    setPasswordStrength({ score, feedback });
    return score === 5;
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 2) return 'bg-red-500';
    if (passwordStrength.score <= 3) return 'bg-yellow-500';
    if (passwordStrength.score <= 4) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength.score <= 2) return 'Weak';
    if (passwordStrength.score <= 3) return 'Fair';
    if (passwordStrength.score <= 4) return 'Good';
    return 'Strong';
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    
    // Validate password in real-time
    if (field === 'password') {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      // Validate required fields
      if (!formData.email || !formData.name || !formData.phone || !formData.password) {
        throw new Error('Please fill in all required fields');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Validate phone format (basic check)
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        throw new Error('Please enter a valid phone number');
      }

      // Validate password strength
      if (!validatePassword(formData.password)) {
        throw new Error('Password does not meet security requirements. Please use a stronger password.');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/landlord/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      setResult({
        success: true,
        user: data.user,
        message: 'Account created successfully!'
      });

    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (result?.success) {
    return (
      <div className="w-full max-w-md">
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl text-center text-green-700">Account Created Successfully!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-green-900 font-medium mb-2">Welcome, {result.user.name}!</h4>
                  <div className="text-green-800 text-sm space-y-2">
                    <p><strong>Email:</strong> {result.user.email}</p>
                    <p><strong>Password:</strong> <code className="bg-green-100 px-2 py-1 rounded">{formData.password}</code></p>
                    <p className="text-xs text-green-700 mt-2">
                      You can now use these credentials to log in to your landlord portal.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Button 
              onClick={onBackToLogin} 
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <Card className="shadow-xl">
        <CardHeader>
          <div className="flex items-center mb-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onBackToLogin}
              className="mr-2 p-1"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <CardTitle className="text-xl">Create Landlord Account</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center">
                <Mail className="h-4 w-4 mr-2" />
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="name" className="flex items-center">
                <User className="h-4 w-4 mr-2" />
                Full Name *
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => {
                  handleChange('name', e.target.value);
                  // Auto-generate password when name or phone changes
                  setTimeout(generateStrongPassword, 100);
                }}
                required
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="phone" className="flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                Phone Number *
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={(e) => {
                  handleChange('phone', e.target.value);
                  // Auto-generate password when name or phone changes
                  setTimeout(generateStrongPassword, 100);
                }}
                required
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center">
                <Lock className="h-4 w-4 mr-2" />
                Password *
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  required
                  disabled={loading}
                  className="pr-20"
                />
                <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    className="h-auto p-1 hover:bg-gray-100"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={generateStrongPassword}
                    className="h-auto p-1 hover:bg-gray-100"
                    title="Generate Strong Password"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Password Strength:</span>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score <= 2 ? 'text-red-600' :
                      passwordStrength.score <= 3 ? 'text-yellow-600' :
                      passwordStrength.score <= 4 ? 'text-blue-600' :
                      'text-green-600'
                    }`}>
                      {getPasswordStrengthText()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {/* Password Requirements */}
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-gray-700">Password Requirements:</p>
                <div className="grid grid-cols-1 gap-1">
                  <div className={`text-xs flex items-center ${
                    formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <span className="mr-1">{formData.password.length >= 8 ? '✓' : '○'}</span>
                    At least 8 characters
                  </div>
                  <div className={`text-xs flex items-center ${
                    /[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <span className="mr-1">{/[A-Z]/.test(formData.password) ? '✓' : '○'}</span>
                    One uppercase letter (A-Z)
                  </div>
                  <div className={`text-xs flex items-center ${
                    /[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <span className="mr-1">{/[a-z]/.test(formData.password) ? '✓' : '○'}</span>
                    One lowercase letter (a-z)
                  </div>
                  <div className={`text-xs flex items-center ${
                    /[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <span className="mr-1">{/[0-9]/.test(formData.password) ? '✓' : '○'}</span>
                    One number (0-9)
                  </div>
                  <div className={`text-xs flex items-center ${
                    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\?]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <span className="mr-1">{/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\?]/.test(formData.password) ? '✓' : '○'}</span>
                    One symbol (!@#$%^&*...)
                  </div>
                </div>
              </div>
              
              {/* Generate Strong Password Button */}
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateStrongPassword}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  <RefreshCw className="h-3 w-3 mr-2" />
                  Generate Strong Password
                </Button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Password Security:</h4>
            <div className="text-xs text-blue-800 space-y-1">
              <p>• <strong>Strong passwords required</strong> for account security</p>
              <p>• Must include: 8+ characters, uppercase, lowercase, number, symbol</p>
              <p>• Use the "Generate Strong Password" button for a secure password</p>
              <p>• You can change your password anytime after account creation</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUp;